import { fetchApi, type TeamSettingsPayload } from '@/lib/api';
import type { TeamMember } from '@/stores/team';
import type { Role } from '@shared/permissions';

/** /api/team/members, /api/team/invites and /api/team/settings (decision C18). */

type RequestOptions = { signal?: AbortSignal };

export interface TeamInvite {
  id: string;
  email: string;
  role: Role;
  status: string;
  invited_by_name: string;
  invited_by_photourl?: string | null;
  /** Set when the invitee already uses jflo with another store. */
  invitee_photourl?: string | null;
  created_at: string;
  expires_at: string;
}

export function listTeamMembers(options: RequestOptions = {}) {
  return fetchApi<{ data: { members: TeamMember[] } }>('/team/members', { signal: options.signal });
}

export function updateTeamMemberRole(userId: string, role: Role) {
  return fetchApi<{ success: boolean }>(`/team/members/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  });
}

export function removeTeamMember(userId: string) {
  return fetchApi<{ success: boolean }>(`/team/members/${userId}`, { method: 'DELETE' });
}

export function listTeamInvites(options: RequestOptions = {}) {
  return fetchApi<{ invites: TeamInvite[] }>('/team/invites', { signal: options.signal });
}

export function sendTeamInvite(invite: { email: string; role: Role }) {
  return fetchApi<{ invite: TeamInvite }>('/team/invites', {
    method: 'POST',
    body: JSON.stringify(invite),
  });
}

export function cancelTeamInvite(inviteId: string) {
  return fetchApi<{ success: boolean }>(`/team/invites/${inviteId}`, { method: 'DELETE' });
}

/** The team's settings: number format, currency, timezone, theme and each feature's own. */
export function getTeamSettingsApi(options: RequestOptions = {}) {
  return fetchApi<{ settings: TeamSettingsPayload }>('/team/settings', { signal: options.signal });
}

/** Adds one site to the Chrome extension's tracked sites (T-77) → the list now. */
export function addTrackedSiteApi(site: string) {
  return fetchApi<{ site: string; sites: string[]; added: boolean }>('/team/time-tracking-sites', {
    method: 'POST',
    body: JSON.stringify({ site }),
  });
}

/** Saves the settings given; the Worker merges them into what's saved. */
export function updateTeamSettingsApi(settings: Partial<TeamSettingsPayload>) {
  return fetchApi<{ success: boolean; message: string }>('/team/settings', {
    method: 'POST',
    body: JSON.stringify({ settings }),
  });
}
